// Vario-Slot: products.articles.detail.tabs.listings.detail.{VARIO_APP_ID}
//
// Zwei Pfade analog der alten App + UX-Wunsch 2026-05-20:
//   A) Aufschalten — EAN-Suche im Kaufland-Katalog
//      → Treffer: User uebernimmt das bestehende Angebot mit der Katalog-Kategorie
//      → Kein Treffer: Wechsel zu B
//   B) Neu listen — Decide/Vorschlaege + manuelle Kategorie-Suche, dann Assign
//
// Beide Pfade enden in: PATCH /erp/articles/{aid}/listings/{lid} mit
// categoryRefs[0] = <vario-cat-id> (resolved via externalReference). Damit
// taucht das Listing im naechsten Inbox-Scan auf.
//
// URL-Params (Vario): id = articleId, listingId = listingId.

import type { SupabaseClient } from '@supabase/supabase-js';
import { renderListingDetailPane } from './shared/listing-detail-pane.js';

interface SlotCtx {
  contentEl: HTMLElement;
  supabase: SupabaseClient;
  session: { token: string; tenantId: string; marketplaceKind: string };
  supabaseUrl: string;
}

interface LoadResponse {
  storefront: string;
  salesChannelId: string;
  current: { varioCategoryId: string; label: string | null; kauflandCategoryId: number | null } | null;
  decideInput: { title: string; description: string; ean: string | null; manufacturer: string | null; price: number };
  /** Vario-SKU des Artikels (article.articleNumber). Wird beim Aufschalten-Upsert
   *  als Kaufland-Pflichtfeld id_offer mitgeschickt. null wenn der Vario-Artikel
   *  noch keine Nummer hat. */
  articleNumber: string | null;
  /** true wenn der Vario-Artikel ein Variantenhauptartikel ist
   *  (productType='VARIANT_MAIN_ARTICLE'). Wandert beim Upsert in
   *  kaufland.listings.is_variant_parent. */
  isVariantParent?: boolean;
  hasCacheForStorefront: boolean;
}

interface FindByEanResponse {
  found: boolean;
  ean: string;
  storefront: string;
  idProduct?: string;
  idCategory?: number | null;
  title?: string | null;
  productUrl?: string | null;
  category?: { id: number | null; name: string | null; title_singular: string | null; level?: number | null } | null;
}

interface DecideResponse {
  suggestions: Array<{
    kauflandCategoryId: number;
    confidence: number | null;
    name: string | null;
    title_singular: string | null;
    level: number | null;
  }>;
}

interface SearchRow {
  category_id: number;
  name: string;
  title_singular: string | null;
  level: number;
}

/** Gueltige GTIN: 8/12/13/14 Ziffern + GS1-Mod-10-Pruefziffer. Gleiche Logik wie in
 *  der Edge Function kaufland-find-product-by-ean-v2 — Kaufland lehnt alles andere
 *  mit 400 "Invalid EAN provided" ab, deshalb pruefen wir vor dem Call und zeigen
 *  sofort eine verstaendliche Meldung. */
function isValidGtin(ean: string): boolean {
  if (!/^\d+$/.test(ean)) return false;
  if (![8, 12, 13, 14].includes(ean.length)) return false;
  let sum = 0;
  for (let i = 0; i < ean.length - 1; i += 1) {
    const digit = ean.charCodeAt(ean.length - 2 - i) - 48;
    sum += digit * (i % 2 === 0 ? 3 : 1);
  }
  return (10 - (sum % 10)) % 10 === ean.charCodeAt(ean.length - 1) - 48;
}

const STOREFRONT_LABELS: Record<string, string> = {
  de: 'Deutschland', at: 'Österreich', cz: 'Tschechien', sk: 'Slowakei',
  pl: 'Polen', fr: 'Frankreich', it: 'Italien',
};

export async function renderListingStatus(ctx: SlotCtx) {
  ctx.contentEl.hidden = false;
  ctx.contentEl.innerHTML = renderShell();

  const params = readSlotParams();
  if (!params.articleId || !params.listingId) {
    setStatusBar(ctx.contentEl, 'error', `URL-Parameter unvollstaendig. articleId=${params.articleId ?? '∅'}, listingId=${params.listingId ?? '∅'}`);
    return;
  }

  const supabaseUrl = ctx.supabaseUrl;

  let state: LoadResponse | null = null;
  let mode: 'choice' | 'lookup' | 'new-listing' | 'release-images' = 'choice';
  // Lokaler Cache der Bilder-Liste fuer die release-images-View. Gehalten zwischen
  // Lade-Aufruf und Save-Aufruf, damit der Save nur die Aenderungen mitschickt.
  type ImageRow = {
    resourceId: string; alias: string;
    description: string | null;     // Vario-DMS ShelfDocument.description (= Alt-Text)
    altText: string;                // editierbarer Wert im UI (vorbefuellt aus description)
    mimeType: string; fileSize: number | null;
    released: boolean;
    sortOrder: number | null;
    varioUiSort: number | null;
    publicUrl: string | null;
  };
  let imageRows: ImageRow[] = [];
  // Wenn der User auf „Kategorie aendern" klickt, ueberbruecken wir die Ready-Box
  // und zeigen wieder die Choice-Cards, obwohl state.current gesetzt ist.
  let forceChoice = false;
  // Status der zugehoerigen Inbox-Row in kaufland.listings — null wenn das Listing noch
  // nicht in unserer Inbox steht (Slot-Direkt-Upsert ist noch nicht gelaufen, oder es
  // ist archived). Steuert zusammen mit state.current ob der Configure-Block angezeigt wird.
  let inboxStatus: string | null = null;
  // PK der Inbox-Row in kaufland.listings — braucht das eingebettete Detail-Pane.
  let inboxListingId: string | null = null;
  // True wenn der Vario-Article fuer DIESE storefront ein article_mapping zu einer
  // aktiven Kaufland-Unit hat (= laufendes Angebot auf Kaufland). Zeigt die gruene
  // Pille "(laufendes Angebot)" neben der Kaufland-Kategorie an.
  let isLiveOnKaufland = false;
  // Letztes EAN-Lookup-Ergebnis — wird in doLookup() gesetzt und in doAssign()
  // ausgewertet, um den Attach-Modus (Aufschalten auf existing id_product) zu
  // erkennen. Bleibt null im Neu-Listen-Pfad und nach Verlassen des Lookup-Modus.
  let lookupResult: FindByEanResponse | null = null;

  await load();

  // ---------------------------------------------------------------------
  async function load() {
    setStatusBar(ctx.contentEl, 'loading', 'Lade Listing-Daten …');
    const res = await callFn<LoadResponse>('vario-listing-category', {
      action: 'load',
      articleId: params.articleId,
      listingId: params.listingId,
    });
    if (!res.ok) {
      setStatusBar(ctx.contentEl, 'error', `Konnte Listing nicht laden: ${res.error}`);
      return;
    }
    state = res.value;
    setSubtitle(`${(STOREFRONT_LABELS[state.storefront] ?? state.storefront).toUpperCase()} · ${state.storefront.toUpperCase()}${state.hasCacheForStorefront ? '' : ' · ⚠ Kategorien-Cache leer'}`);
    setStatusBar(ctx.contentEl, '', '');
    await Promise.all([loadInboxStatus(), loadLiveStatus()]);
    renderCurrent();
    renderModeUi();
  }

  async function loadLiveStatus() {
    if (!state) { isLiveOnKaufland = false; return; }
    // Schritt 1: mapping fuer (article, storefront) finden. RLS via active_tenant_id.
    const { data: mapping, error: mErr } = await ctx.supabase
      .schema('vario')
      .from('article_mappings')
      .select('marketplace_unit_id')
      .eq('vario_article_id', params.articleId!)
      .eq('storefront', state.storefront)
      .eq('marketplace', 'kaufland')
      .maybeSingle();
    if (mErr) {
      console.warn('[listing-status] mapping lookup failed:', mErr.message);
      isLiveOnKaufland = false;
      return;
    }
    const unitId = (mapping as { marketplace_unit_id: string } | null)?.marketplace_unit_id;
    if (!unitId) { isLiveOnKaufland = false; return; }
    // Schritt 2: gemappte unit pruefen — nur "active" + nicht geloescht zaehlt als laufend.
    const { data: unit, error: uErr } = await ctx.supabase
      .schema('kaufland')
      .from('units')
      .select('status,deleted_at')
      .eq('id_unit', unitId)
      .eq('storefront', state.storefront)
      .maybeSingle();
    if (uErr) {
      console.warn('[listing-status] unit lookup failed:', uErr.message);
      isLiveOnKaufland = false;
      return;
    }
    const u = unit as { status: string; deleted_at: string | null } | null;
    isLiveOnKaufland = u != null && u.status === 'active' && u.deleted_at === null;
  }

  async function loadInboxStatus() {
    // RLS scoped via active_tenant_id-Claim — wir bekommen nur Rows des eigenen Tenants.
    const { data, error } = await ctx.supabase
      .schema('kaufland')
      .from('listings')
      .select('id, status')
      .eq('vario_listing_id', params.listingId!)
      .maybeSingle();
    if (error) {
      console.warn('[listing-status] inbox lookup failed:', error.message);
      inboxStatus = null;
      inboxListingId = null;
      return;
    }
    const row = data as { id: string; status: string } | null;
    inboxStatus = row?.status ?? null;
    inboxListingId = row?.id ?? null;
  }

  function renderCurrent() {
    if (!state) return;
    const sec = ctx.contentEl.querySelector<HTMLElement>('#current-section')!;
    if (state.current) {
      sec.hidden = false;
      const labelEl = sec.querySelector<HTMLElement>('#current-label')!;
      labelEl.textContent = state.current.label ?? `Vario #${state.current.varioCategoryId}`;
      // Pille "(laufendes Angebot)" als inline-Tag rechts vom Label, nur wenn der
      // Article in DIESEM Storefront-Channel eine aktive Kaufland-Unit hat.
      const livePill = sec.querySelector<HTMLElement>('#current-live-pill')!;
      livePill.hidden = !isLiveOnKaufland;
      const parts: string[] = [];
      if (state.current.kauflandCategoryId) parts.push(`Kaufland-ID: ${state.current.kauflandCategoryId}`);
      parts.push(`Vario-ID: ${state.current.varioCategoryId}`);
      sec.querySelector<HTMLElement>('#current-secondary')!.textContent = parts.join(' · ');
    } else {
      sec.hidden = true;
    }
  }

  function renderModeUi() {
    const choice = ctx.contentEl.querySelector<HTMLElement>('#mode-choice')!;
    const configure = ctx.contentEl.querySelector<HTMLElement>('#mode-configure')!;
    const lookup = ctx.contentEl.querySelector<HTMLElement>('#mode-lookup')!;
    const newL = ctx.contentEl.querySelector<HTMLElement>('#mode-new-listing')!;
    const relImg = ctx.contentEl.querySelector<HTMLElement>('#mode-release-images')!;

    // Configure-Modus: Kategorie ist im Vario-Listing zugeordnet UND der Artikel steht in
    // unserer Inbox (kaufland.listings, nicht archived) UND der User hat nicht „aendern"
    // gewaehlt. Ohne Inbox-Row faellt der Slot in die alten Choice-Cards zurueck — sonst
    // wuerde das Configure-Pane auch fuer Artikel angezeigt, die der Lister gar nicht
    // kennt (z.B. wenn der Scan-Job aktuell nicht durchlaeuft).
    const inInbox = inboxStatus !== null && inboxStatus !== 'archived';
    const showConfigure = mode === 'choice' && Boolean(state?.current) && inInbox && !forceChoice;
    choice.hidden = mode !== 'choice' || showConfigure;
    configure.hidden = !showConfigure;
    lookup.hidden = mode !== 'lookup';
    newL.hidden = mode !== 'new-listing';
    relImg.hidden = mode !== 'release-images';

    // Der Lister ist vollständig hier im Artikel-Slot verfügbar (Configure-Pane). Der
    // frühere "Im Lister öffnen"-Sprung auf die navigation.root-Seite `kaufland-page`
    // wurde 2026-06-14 entfernt: navigation.root ist abgebaut, der Lister ist ein
    // eigener Verkaufskanal-Tab (/#/list). Kein Cross-Window-Deep-Link mehr nötig.
    if (showConfigure) {
      mountConfigurePane();
    }

    if (mode === 'choice') {
      const eanLine = ctx.contentEl.querySelector<HTMLElement>('#choice-ean')!;
      const noEan = ctx.contentEl.querySelector<HTMLElement>('#choice-no-ean')!;
      const searchBtn = ctx.contentEl.querySelector<HTMLButtonElement>('#btn-lookup')!;
      const choiceMan = ctx.contentEl.querySelector<HTMLElement>('#choice-manufacturer')!;
      // Variantenvater: in Vario traegt der Hauptartikel KEINE EAN — die EANs liegen an
      // den Kind-Varianten. Der Vater wird bei Kaufland auch nicht selbst als Produkt
      // gelistet (jede Variante ist ein eigenes Kaufland-Produkt mit eigener EAN), er ist
      // nur der Einstieg fuer die Varianten-Anlage. Deshalb ist die fehlende EAN hier kein
      // Fehler, und "Aufschalten per EAN-Suche" ist gegenstandslos.
      const isParent = state?.isVariantParent === true;
      const parentCard = ctx.contentEl.querySelector<HTMLElement>('#choice-card-attach')!;
      const parentNote = ctx.contentEl.querySelector<HTMLElement>('#choice-variant-parent-note')!;
      parentCard.hidden = isParent;
      parentNote.hidden = !isParent;
      if (state?.decideInput.ean) {
        eanLine.textContent = state.decideInput.ean;
        noEan.hidden = true;
        searchBtn.disabled = false;
      } else if (isParent) {
        eanLine.textContent = '— (liegt an den Varianten)';
        noEan.hidden = true;
        searchBtn.disabled = true;
      } else {
        eanLine.textContent = '—';
        noEan.hidden = false;
        searchBtn.disabled = true;
      }
      choiceMan.textContent = state?.decideInput.manufacturer ?? '—';
      const manWarn = ctx.contentEl.querySelector<HTMLElement>('#choice-no-manufacturer')!;
      manWarn.hidden = Boolean(state?.decideInput.manufacturer);
    }
    if (mode === 'new-listing') {
      renderInputPreview();
    }
  }

  // Detail-Pane in den Configure-Block mounten. Guard via data-listing-id:
  // renderModeUi() laeuft bei jedem Modus-Wechsel — das Pane soll nur neu
  // rendern, wenn sich die Inbox-Row geaendert hat (sonst verliert der User
  // Formular-/Tab-State). doAssign() loescht das Attribut explizit, damit
  // ein Kategorie-Wechsel ein frisches Pane erzwingt.
  function mountConfigurePane(): void {
    const paneEl = ctx.contentEl.querySelector<HTMLDivElement>('#configure-pane');
    if (!paneEl || !inboxListingId) return;
    if (paneEl.dataset['listingId'] === inboxListingId) return;
    paneEl.dataset['listingId'] = inboxListingId;
    void renderListingDetailPane(paneEl, {
      supabase: ctx.supabase,
      session: { token: ctx.session.token, tenantId: ctx.session.tenantId },
      supabaseUrl: ctx.supabaseUrl,
    }, inboxListingId, {
      kind: 'slot',
      onListingChanged: () => { void loadInboxStatus(); },
      onDeleted: () => {
        delete paneEl.dataset['listingId'];
        // Listing ist weg — zurueck zu den Choice-Cards (Kategorie neu zuweisen).
        forceChoice = true;
        void load();
      },
    });
  }

  function renderInputPreview() {
    const host = ctx.contentEl.querySelector<HTMLElement>('#input-preview');
    if (!host || !state) return;
    const { title, description, ean, manufacturer } = state.decideInput;
    const parts: string[] = [];
    if (title) parts.push(`<strong>Titel:</strong> ${escapeHtml(title)}`);
    if (ean) parts.push(`<strong>EAN:</strong> ${escapeHtml(ean)}`);
    // Variantenvater: keine EAN am Hauptartikel erwartet (siehe renderModeUi) — die
    // Kaufland-Produkte sind die Varianten, jede mit eigener EAN. Neutraler Hinweis
    // statt roter Fehlermeldung, sonst liest der Kunde einen Blocker, den es nicht gibt.
    else if (state.isVariantParent === true) parts.push(`<strong>EAN:</strong> <span class="v-text-muted">— nicht nötig, die EANs liegen an den Varianten</span>`);
    else parts.push(`<strong>EAN:</strong> <span class="v-text-error">— Bitte EAN in den Artikeldetails oder in diesem Listing weiter unten pflegen.</span>`);
    if (manufacturer) parts.push(`<strong>Hersteller:</strong> ${escapeHtml(manufacturer)}`);
    else parts.push(`<strong>Hersteller:</strong> <span class="v-text-error">— Bitte Hersteller in den Artikeldetails pflegen.</span>`);
    if (description) parts.push(`<strong>Beschreibung:</strong> ${escapeHtml(description.slice(0, 200))}${description.length > 200 ? '…' : ''}`);
    if (parts.length === 0) { host.hidden = true; return; }
    host.hidden = false;
    host.innerHTML = parts.join('<br>');
  }

  // ---------------------------------------------------------------------
  // Event-Wiring
  // ---------------------------------------------------------------------
  ctx.contentEl.querySelector<HTMLButtonElement>('#btn-reassign')!.addEventListener('click', () => {
    forceChoice = true;
    mode = 'choice';
    renderModeUi();
  });
  ctx.contentEl.querySelector<HTMLButtonElement>('#btn-release-images')!.addEventListener('click', () => {
    mode = 'release-images';
    renderModeUi();
    void doLoadImages();
  });
  ctx.contentEl.querySelector<HTMLButtonElement>('#btn-back-from-images')!.addEventListener('click', () => {
    mode = 'choice';
    renderModeUi();
  });
  ctx.contentEl.querySelector<HTMLButtonElement>('#btn-save-images')!.addEventListener('click', () => void doSaveImages());
  ctx.contentEl.querySelector<HTMLButtonElement>('#btn-lookup')!.addEventListener('click', () => void doLookup());
  ctx.contentEl.querySelector<HTMLButtonElement>('#btn-new-listing')!.addEventListener('click', () => { mode = 'new-listing'; renderModeUi(); });
  ctx.contentEl.querySelector<HTMLButtonElement>('#btn-back-to-choice-1')!.addEventListener('click', () => { lookupResult = null; mode = 'choice'; renderModeUi(); });
  ctx.contentEl.querySelector<HTMLButtonElement>('#btn-back-to-choice-2')!.addEventListener('click', () => { mode = 'choice'; renderModeUi(); });
  ctx.contentEl.querySelector<HTMLButtonElement>('#btn-decide')!.addEventListener('click', () => void doDecide());
  ctx.contentEl.querySelector<HTMLButtonElement>('#btn-lookup-take-over')!.addEventListener('click', () => {
    const b = ctx.contentEl.querySelector<HTMLButtonElement>('#btn-lookup-take-over')!;
    const idCat = Number(b.dataset['kcat']);
    if (Number.isFinite(idCat) && idCat > 0) void doAssign(idCat, b, b.dataset['kcatname'] ?? null);
  });
  ctx.contentEl.querySelector<HTMLButtonElement>('#btn-lookup-new-instead')!.addEventListener('click', () => { lookupResult = null; mode = 'new-listing'; renderModeUi(); });
  const searchInput = ctx.contentEl.querySelector<HTMLInputElement>('#search-input')!;
  let debounce: ReturnType<typeof setTimeout> | null = null;
  searchInput.addEventListener('input', () => {
    if (debounce) clearTimeout(debounce);
    const q = searchInput.value;
    debounce = setTimeout(() => void doSearch(q), 250);
  });

  // ---------------------------------------------------------------------
  async function doLookup() {
    if (!state?.decideInput.ean) return;
    mode = 'lookup';
    lookupResult = null;
    renderModeUi();
    const resultHost = ctx.contentEl.querySelector<HTMLElement>('#lookup-result')!;
    const eanRaw = state.decideInput.ean.trim();
    if (!isValidGtin(eanRaw)) {
      resultHost.innerHTML = `
        <div class="v-alert v-alert--warn">
          <strong>EAN ungültig.</strong> „${escapeHtml(eanRaw)}" ist keine gültige EAN/GTIN
          (erlaubt: 8, 12, 13 oder 14 Ziffern mit korrekter Prüfziffer).<br>
          Bitte die EAN am Vario-Artikel korrigieren und danach erneut suchen.
        </div>`;
      return;
    }
    resultHost.innerHTML = `<div class="v-text-muted v-pa-sm">Suche im Kaufland-Katalog nach EAN ${escapeHtml(state.decideInput.ean)} …</div>`;
    // v2-Slug: MCP-Deploy auf den alten Slug failt aktuell mit "internal error".
    // v2 enthaelt zusaetzlich Kategorie-Cache-Resolve + productUrl.
    const res = await callFn<FindByEanResponse>('kaufland-find-product-by-ean-v2', {
      storefront: state.storefront,
      ean: state.decideInput.ean,
    });
    if (!res.ok) {
      resultHost.innerHTML = `<div class="v-text-error v-pa-sm">Lookup fehlgeschlagen: ${escapeHtml(res.error)}</div>`;
      return;
    }
    const v = res.value;
    lookupResult = v;
    if (!v.found) {
      resultHost.innerHTML = `
        <div class="v-alert v-alert--info">
          <strong>Kein Treffer.</strong> Diese EAN ist nicht im Kaufland-Katalog.<br>
          Du kannst den Artikel neu listen — wir schlagen eine Kategorie vor.
        </div>`;
      return;
    }
    const catLabel = v.category?.title_singular ?? v.category?.name ?? null;
    const catCell = catLabel
      ? `${escapeHtml(catLabel)}${v.category?.level != null ? ` <span style="opacity:0.7;">· Level ${v.category.level}</span>` : ''} <code style="opacity:0.7;">(ID ${v.idCategory})</code>`
      : (v.idCategory ? `<code>ID ${v.idCategory}</code> <em style="opacity:0.7;">(Cache leer — Kategorie aktualisieren)</em>` : '—');
    const takeOverBtn = ctx.contentEl.querySelector<HTMLButtonElement>('#btn-lookup-take-over')!;
    takeOverBtn.dataset['kcat'] = String(v.idCategory ?? '');
    takeOverBtn.dataset['kcatname'] = catLabel ?? '';
    takeOverBtn.disabled = !v.idCategory;
    const openBtn = v.productUrl
      ? ` <a href="${escapeHtml(v.productUrl)}" target="_blank" rel="noopener noreferrer" class="v-icon-only" title="Im Kaufland-Katalog oeffnen" aria-label="Im Kaufland-Katalog oeffnen" style="margin-left:0.4em;color:inherit;text-decoration:none;">
          <span class="v-icon fa-regular fa-arrow-up-right-from-square"></span>
        </a>`
      : '';
    resultHost.innerHTML = `
      <div class="v-alert v-alert--ok">
        <strong>Artikel im Kaufland-Katalog gefunden.</strong>
      </div>
      <table class="v-table" style="width:100%;font-size:0.9em;margin-top:0.5em;">
        <tbody>
          <tr><th style="text-align:left;width:35%;">Kaufland-Produkt</th><td>${escapeHtml(v.title ?? '—')}</td></tr>
          <tr><th style="text-align:left;">Produkt-ID</th><td><code>${escapeHtml(v.idProduct ?? '')}</code>${openBtn}</td></tr>
          <tr><th style="text-align:left;">Kategorie</th><td>${catCell}</td></tr>
        </tbody>
      </table>
      <div style="font-size:0.85em;color:var(--v-color-text-muted,#666);margin-top:0.4em;">
        Aufschalten uebernimmt die Kaufland-Kategorie und schreibt sie an dein Vario-Listing zurueck.
      </div>
    `;
  }

  async function doDecide() {
    if (!state) return;
    const btn = ctx.contentEl.querySelector<HTMLButtonElement>('#btn-decide')!;
    btn.disabled = true;
    setStatusBar(ctx.contentEl, 'loading', 'Frage Kaufland nach Vorschlaegen …');
    ctx.contentEl.querySelector<HTMLElement>('#suggestions')!.innerHTML = '';
    try {
      const { title, description, price, manufacturer } = state.decideInput;
      const payload: Record<string, unknown> = {
        storefront: state.storefront,
        title,
        description,
        price,
      };
      if (manufacturer) payload['manufacturer'] = manufacturer;
      // v3-Slug: alte kaufland-decide-category liess sich nicht mehr updaten (transient
      // Deploy-Lock), v3 enthaelt manufacturer-Pflichtfeld + brand-Optional.
      const res = await callFn<DecideResponse>('kaufland-decide-category-v3', payload);
      if (!res.ok) {
        setStatusBar(ctx.contentEl, 'error', `Vorschlaege fehlgeschlagen: ${res.error}`);
        return;
      }
      renderSuggestions(res.value.suggestions);
      if (res.value.suggestions.length === 0) {
        setStatusBar(ctx.contentEl, 'error', 'Kaufland hat keine Vorschlaege geliefert — bitte manuell suchen.');
      } else {
        setStatusBar(ctx.contentEl, '', '');
      }
    } finally {
      btn.disabled = false;
    }
  }

  function renderSuggestions(suggestions: DecideResponse['suggestions']) {
    const c = ctx.contentEl.querySelector<HTMLElement>('#suggestions')!;
    if (suggestions.length === 0) { c.innerHTML = '<div class="v-text-muted v-pa-sm">Keine Vorschlaege.</div>'; return; }
    c.innerHTML = suggestions.map((s) => {
      const conf = s.confidence != null ? `${Math.round(Number(s.confidence) * 100)}%` : '—';
      const name = s.title_singular ?? s.name ?? `#${s.kauflandCategoryId}`;
      return `
        <div style="display:flex;align-items:center;gap:0.6em;padding:0.5em;border-bottom:1px solid var(--v-color-border-soft,#eee);">
          <div style="flex:1;min-width:0;">
            <div style="font-weight:600;">${escapeHtml(name)}</div>
            <div style="font-size:0.78em;color:var(--v-color-text-muted,#666);">Kaufland-ID ${s.kauflandCategoryId}${s.level != null ? ` · Level ${s.level}` : ''} · Confidence ${conf}</div>
          </div>
          <button type="button" class="v-button" data-kcat="${s.kauflandCategoryId}" data-kcatname="${escapeHtml(name)}">
            <span class="v-icon fa-regular fa-check"></span> Uebernehmen
          </button>
        </div>`;
    }).join('');
    c.querySelectorAll<HTMLButtonElement>('button[data-kcat]').forEach((b) => {
      b.addEventListener('click', () => { const id = Number(b.dataset['kcat']); void doAssign(id, b, b.dataset['kcatname'] ?? null); });
    });
  }

  async function doSearch(q: string) {
    const c = ctx.contentEl.querySelector<HTMLElement>('#search-results')!;
    if (!state) return;
    if (!q || q.trim().length < 2) { c.innerHTML = '<div class="v-text-muted v-pa-sm">Mindestens 2 Zeichen eingeben …</div>'; return; }
    c.innerHTML = '<div class="v-text-muted v-pa-sm">Suche laeuft …</div>';
    const { data, error } = await ctx.supabase.rpc('kaufland_search_categories', { _storefront: state.storefront, _q: q, _limit: 30 });
    if (error) { c.innerHTML = `<div class="v-text-error v-pa-sm">Suche fehlgeschlagen: ${escapeHtml(error.message)}</div>`; return; }
    const rows = (data ?? []) as SearchRow[];
    if (rows.length === 0) { c.innerHTML = '<div class="v-text-muted v-pa-sm">Keine Treffer.</div>'; return; }
    c.innerHTML = rows.map((r) => {
      const name = r.title_singular ?? r.name ?? `#${r.category_id}`;
      return `
        <div style="display:flex;align-items:center;gap:0.6em;padding:0.5em;border-bottom:1px solid var(--v-color-border-soft,#eee);">
          <div style="flex:1;min-width:0;">
            <div style="font-weight:600;">${escapeHtml(name)}</div>
            <div style="font-size:0.78em;color:var(--v-color-text-muted,#666);">Kaufland-ID ${r.category_id} · Level ${r.level}</div>
          </div>
          <button type="button" class="v-button" data-kcat="${r.category_id}" data-kcatname="${escapeHtml(name)}">
            <span class="v-icon fa-regular fa-check"></span> Uebernehmen
          </button>
        </div>`;
    }).join('');
    c.querySelectorAll<HTMLButtonElement>('button[data-kcat]').forEach((b) => {
      b.addEventListener('click', () => { const id = Number(b.dataset['kcat']); void doAssign(id, b, b.dataset['kcatname'] ?? null); });
    });
  }

  async function doAssign(kauflandCategoryId: number, btn: HTMLButtonElement, displayName: string | null = null) {
    setStatusBar(ctx.contentEl, 'loading', 'Uebernehme Kategorie …');
    btn.disabled = true;
    const res = await callFn<{ ok: boolean; varioCategoryId: string }>('vario-listing-category', {
      action: 'assign',
      articleId: params.articleId,
      listingId: params.listingId,
      kauflandCategoryId,
    });
    if (!res.ok) { setStatusBar(ctx.contentEl, 'error', `Zuweisung fehlgeschlagen: ${res.error}`); btn.disabled = false; return; }

    // Lister-Inbox direkt schreiben — wir kennen alle Daten aus state + assign-response.
    // Kein scan-roundtrip noetig. await damit das anschliessende load() den frisch
    // geschriebenen inboxStatus sieht und die Ready-Box korrekt einblendet; fail-soft
    // bleibt (UI-Erfolg ist schon gesetzt, Upsert-Fehler nur geloggt). _kaufland_category_id
    // kommt direkt aus dem klick-aufruf — der mapping-editor (stufe 4) braucht das pflicht.
    //
    // Attach-Pfad: wenn der User aus dem Lookup-Treffer "Auf dieses Angebot aufschalten"
    // klickt, kennen wir die Kaufland-id_product aus dem letzten EAN-Lookup. Wir
    // schalten den Lister auf _mode='attach' und reichen _attach_id_product durch —
    // die RPC (Migration 0113) speichert das am Listing, damit der Push spaeter den
    // Aufschalt-Flow statt Neu-Anlage waehlt. Neu-Listen-Pfad (lookupResult null oder
    // not-found) bleibt bei den RPC-Defaults (_mode='new', _attach_id_product=null).
    const isAttach = !!lookupResult?.found && !!lookupResult?.idProduct;
    if (state) {
      const upsertArgs: Record<string, unknown> = {
        _storefront: state.storefront,
        _vario_article_id: params.articleId,
        _vario_listing_id: params.listingId,
        _vario_category_id: res.value.varioCategoryId,
        _kaufland_category_id: kauflandCategoryId,
        _vario_channel_id: state.salesChannelId,
        _vario_article_name: state.decideInput.title || null,
        _vario_article_number: state.articleNumber,
        _ean: state.decideInput.ean,
        _description: state.decideInput.description,
        _category_name: displayName,
        _is_variant_parent: state.isVariantParent ?? false,
      };
      if (isAttach && lookupResult?.idProduct) {
        upsertArgs['_mode'] = 'attach';
        upsertArgs['_attach_id_product'] = lookupResult.idProduct;
      }
      const { error: upsertError } = await ctx.supabase.rpc('kaufland_upsert_listing_from_slot', upsertArgs);
      if (upsertError) console.warn('[listing-status] upsert listing failed:', upsertError.message);
    }

    setStatusBar(
      ctx.contentEl,
      'success',
      isAttach
        ? 'Kategorie übernommen — Aufschalt-Konditionen unten ausfüllen.'
        : 'Kategorie zugeordnet — Listing unten konfigurieren.',
    );
    ctx.contentEl.querySelector<HTMLElement>('#suggestions')!.innerHTML = '';
    ctx.contentEl.querySelector<HTMLInputElement>('#search-input')!.value = '';
    ctx.contentEl.querySelector<HTMLElement>('#search-results')!.innerHTML = '';
    ctx.contentEl.querySelector<HTMLElement>('#lookup-result')!.innerHTML = '';
    mode = 'choice';
    forceChoice = false; // Configure-Block wieder einblenden, Choice-Cards weg.
    // Kategorie hat sich geaendert — Configure-Pane beim naechsten Mount frisch rendern.
    const paneEl = ctx.contentEl.querySelector<HTMLDivElement>('#configure-pane');
    if (paneEl) delete paneEl.dataset['listingId'];
    await load();
  }

  // ---------------------------------------------------------------------
  // Bilder-Freigabe (release-images)
  // ---------------------------------------------------------------------

  async function doLoadImages() {
    const host = ctx.contentEl.querySelector<HTMLElement>('#release-images-list')!;
    host.innerHTML = '<div class="v-text-muted v-pa-sm">Lade Vario-DMS-Bilder …</div>';
    type Loaded = Omit<ImageRow, 'altText'>;
    const res = await callFn<{ ok: boolean; images: Loaded[] }>('vario-article-images', {
      articleId: params.articleId,
    });
    if (!res.ok) {
      host.innerHTML = `<div class="v-text-error v-pa-sm">Lade-Fehler: ${escapeHtml(res.error)}</div>`;
      return;
    }
    imageRows = (res.value.images ?? []).map((img) => ({
      ...img,
      altText: typeof img.description === 'string' ? img.description : '',
    }));
    renderImagesList();
  }

  function renderImagesList() {
    const host = ctx.contentEl.querySelector<HTMLElement>('#release-images-list')!;
    if (imageRows.length === 0) {
      host.innerHTML = `
        <div class="v-alert v-alert--info">
          <strong>Keine Bilder am Artikel.</strong><br>
          Bitte lade in Vario unter <em>Artikel-Detail → Anlagen</em> erst Bilder hoch (Typ Bild, jpg/png/gif).
        </div>`;
      return;
    }
    // Pro Eintrag den Slot-Index berechnen — nur die released-Bilder zaehlen, und
    // zwar in der Reihenfolge wie sie hier auftauchen. Slot 0 = Hauptbild,
    // 1..N = Bild 1..N. Wird im Label und im Alt-Text-Placeholder benutzt; ent-
    // spricht 1:1 dem sort_order beim Push.
    let releasedCount = 0;
    const slotLabels = imageRows.map((img) => {
      if (!img.released) return null;
      const slotIdx = releasedCount;
      releasedCount += 1;
      return slotIdx === 0 ? 'Hauptbild' : `Bild ${slotIdx}`;
    });

    host.innerHTML = imageRows.map((img, idx) => {
      const checked = img.released ? 'checked' : '';
      const sizeKb = img.fileSize ? `${Math.round(img.fileSize / 1024)} KB` : '';
      const slotLabel = slotLabels[idx];
      const slotBadge = slotLabel
        ? `<span style="display:inline-block;padding:0.1em 0.45em;margin-right:0.3em;background:#e8f3d6;color:#84b933;border-radius:4px;font-size:0.82em;font-weight:600;">${slotLabel}</span>`
        : '';
      const altPlaceholder = slotLabel ? `Alt-Text fuer ${slotLabel} …` : 'Alt-Text fuer Screenreader / SEO …';
      // Freigegebenes Bild → direkter <img src=publicUrl> (Vario-Public-URL inkl.
      // /api/vario/public/dms/-Prefix, vom Worker korrekt gebaut). Nicht freigegeben →
      // ueber den DMS-Image-Proxy mit Skeleton-Shimmer.
      const skeletonStyle = 'background:linear-gradient(110deg,#f0f0f0 8%,#e3e3e3 18%,#f0f0f0 33%);background-size:200% 100%;animation:kli-shimmer 1.5s linear infinite;';
      const previewInner = img.publicUrl
        ? `<img src="${escapeHtml(img.publicUrl)}" alt="" style="max-width:64px;max-height:64px;object-fit:cover;border-radius:3px;border:1px solid var(--v-color-border,#ddd);">`
        : `<img class="img-proxy-lazy" data-rid="${escapeHtml(img.resourceId)}" alt="" style="max-width:64px;max-height:64px;object-fit:cover;border-radius:3px;border:1px solid var(--v-color-border,#ddd);${skeletonStyle}">`;
      return `
        <div data-img-idx="${idx}" style="display:flex;align-items:flex-start;gap:0.7em;padding:0.6em;border-bottom:1px solid var(--v-color-border-soft,#eee);">
          <input type="checkbox" class="img-toggle" data-rid="${escapeHtml(img.resourceId)}" ${checked} style="margin-top:0.3em;cursor:pointer;">
          <div style="width:64px;height:64px;display:flex;align-items:center;justify-content:center;flex-shrink:0;">${previewInner}</div>
          <div style="flex:1;min-width:0;display:flex;flex-direction:column;gap:0.3em;">
            <div style="font-weight:600;overflow:hidden;text-overflow:ellipsis;white-space:nowrap;">${slotBadge}${escapeHtml(img.alias)}</div>
            <div style="font-size:0.78em;color:var(--v-color-text-muted,#666);">${escapeHtml(img.mimeType)}${sizeKb ? ` · ${sizeKb}` : ''}</div>
            <input type="text" class="img-alt-text" data-rid="${escapeHtml(img.resourceId)}" value="${escapeHtml(img.altText)}" placeholder="${escapeHtml(altPlaceholder)}" maxlength="250" style="width:100%;padding:0.3em 0.5em;border:1px solid var(--v-color-border,#ddd);border-radius:3px;font-size:0.88em;">
          </div>
          <div style="display:flex;flex-direction:column;gap:0.2em;">
            <button type="button" class="v-button v-button--ghost v-icon-only btn-img-up" data-rid="${escapeHtml(img.resourceId)}" title="Nach oben" ${idx === 0 ? 'disabled' : ''}>
              <span class="v-icon fa-regular fa-arrow-up"></span>
            </button>
            <button type="button" class="v-button v-button--ghost v-icon-only btn-img-down" data-rid="${escapeHtml(img.resourceId)}" title="Nach unten" ${idx === imageRows.length - 1 ? 'disabled' : ''}>
              <span class="v-icon fa-regular fa-arrow-down"></span>
            </button>
          </div>
        </div>`;
    }).join('');
    host.querySelectorAll<HTMLInputElement>('input.img-toggle').forEach((cb) => {
      cb.addEventListener('change', () => {
        const rid = cb.dataset['rid'];
        const row = imageRows.find((r) => r.resourceId === rid);
        if (row) {
          row.released = cb.checked;
          // Rerender, damit die Slot-Labels (Hauptbild / Bild 1..N) live mitlaufen.
          renderImagesList();
        }
      });
    });
    host.querySelectorAll<HTMLInputElement>('input.img-alt-text').forEach((inp) => {
      inp.addEventListener('input', () => {
        const rid = inp.dataset['rid'];
        const row = imageRows.find((r) => r.resourceId === rid);
        if (row) row.altText = inp.value;
      });
    });
    host.querySelectorAll<HTMLButtonElement>('button.btn-img-up').forEach((b) => {
      b.addEventListener('click', () => moveImage(b.dataset['rid'] ?? '', -1));
    });
    host.querySelectorAll<HTMLButtonElement>('button.btn-img-down').forEach((b) => {
      b.addEventListener('click', () => moveImage(b.dataset['rid'] ?? '', +1));
    });
    // Lazy-Load der Proxy-Vorschauen — parallel mit Concurrency-Limit 3.
    void loadPreviewsViaProxy(host);
  }

  async function loadPreviewsViaProxy(host: HTMLElement) {
    const imgs = Array.from(host.querySelectorAll<HTMLImageElement>('img.img-proxy-lazy'));
    // Parallel mit Concurrency-Limit 3 — schneller als sequentiell, ohne Vario zu ueberlasten.
    const queue = imgs.slice();
    async function worker(): Promise<void> {
      while (queue.length > 0) {
        const imgEl = queue.shift();
        if (!imgEl) return;
        const rid = imgEl.dataset['rid'];
        if (!rid) continue;
        // Wenn das Element zwischenzeitlich aus dem DOM geflogen ist (re-render), skip.
        if (!imgEl.isConnected) continue;
        try {
          const res = await fetch(`${supabaseUrl}/functions/v1/vario-dms-image-proxy`, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${ctx.session.token}` },
            body: JSON.stringify({ articleId: params.articleId, resourceId: rid }),
          });
          if (!res.ok) continue;
          const blob = await res.blob();
          if (!imgEl.isConnected) continue;
          imgEl.src = URL.createObjectURL(blob);
          // Shimmer-Skeleton abschalten, sobald das Bild da ist.
          imgEl.style.background = 'none';
          imgEl.style.animation = 'none';
        } catch (e) {
          console.warn('[release-images] preview proxy failed for', rid, (e as Error).message);
        }
      }
    }
    await Promise.all([worker(), worker(), worker()]);
  }

  function moveImage(rid: string, delta: number) {
    const idx = imageRows.findIndex((r) => r.resourceId === rid);
    const newIdx = idx + delta;
    if (idx < 0 || newIdx < 0 || newIdx >= imageRows.length) return;
    const [row] = imageRows.splice(idx, 1);
    if (row) imageRows.splice(newIdx, 0, row);
    renderImagesList();
  }

  async function doSaveImages() {
    const btn = ctx.contentEl.querySelector<HTMLButtonElement>('#btn-save-images')!;
    btn.disabled = true;
    setStatusBar(ctx.contentEl, 'loading', 'Speichere Bilder-Freigabe …');
    // Reihenfolge im UI = sortOrder. Nur freigegebene mitschicken — pro Bild zusaetzlich
    // den editierten Alt-Text (geht ans Vario-DMS als ShelfDocument.description).
    const releases = imageRows
      .filter((r) => r.released)
      .map((r, idx) => ({
        varioResourceId: r.resourceId,
        sortOrder: idx,
        mimeType: r.mimeType,
        alias: r.alias,
        altText: r.altText.trim(),
      }));
    type ReleaseResp = {
      ok: boolean; inserted: number; updated: number; deleted: number;
      enqueuedShareJobs?: number; enqueuedMetaJobs?: number; enqueuedJobs?: number;
    };
    const res = await callFn<ReleaseResp>('kaufland-release-images', { articleId: params.articleId, releases });
    btn.disabled = false;
    if (!res.ok) {
      setStatusBar(ctx.contentEl, 'error', `Speichern fehlgeschlagen: ${res.error}`);
      return;
    }
    const v = res.value;
    const shareJobs = v.enqueuedShareJobs ?? v.enqueuedJobs ?? 0;
    const metaJobs = v.enqueuedMetaJobs ?? 0;
    const tail = [
      shareJobs > 0 ? `${shareJobs} neue Shares` : '',
      metaJobs > 0 ? `${metaJobs} DMS-Updates` : '',
    ].filter(Boolean).join(', ');
    setStatusBar(
      ctx.contentEl,
      'success',
      `Bilder gespeichert (${v.inserted} neu, ${v.updated} sortiert, ${v.deleted} entfernt${tail ? `; ${tail}` : ''}).`,
    );
    mode = 'choice';
    renderModeUi();
  }

  async function callFn<T>(name: string, body: unknown): Promise<{ ok: true; value: T } | { ok: false; error: string }> {
    try {
      const res = await fetch(`${supabaseUrl}/functions/v1/${name}`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${ctx.session.token}` },
        body: JSON.stringify(body),
      });
      const text = await res.text();
      let parsed: unknown;
      try { parsed = text ? JSON.parse(text) : {}; } catch { parsed = { raw: text }; }
      if (!res.ok) {
        const errBag = (parsed as { error?: { code?: string; message?: string; body?: string } } | null)?.error;
        const msg = errBag ? `${errBag.code ?? '?'}: ${errBag.message ?? '?'}${errBag.body ? ` — ${errBag.body}` : ''}` : `HTTP ${res.status}: ${text.slice(0, 500)}`;
        return { ok: false, error: msg };
      }
      return { ok: true, value: parsed as T };
    } catch (e) {
      return { ok: false, error: (e as Error).message };
    }
  }
}

// ---------------------------------------------------------------------------
// Layout
// ---------------------------------------------------------------------------

function renderShell(): string {
  return `
    <style>
      @keyframes kli-shimmer { 0% { background-position: -200% 0; } 100% { background-position: 200% 0; } }

      /* Listing-status-Slot = FESTES Fenster mit internem Scroll. Der Vario-
         Artikel-Bereich ist kuerzer als lange Tabs (Mapping, Bilder, Decide) —
         bei iframe=Inhaltshoehe ragte der untere Teil aus dem sichtbaren Vario-
         Panel und war unerreichbar (Vario reicht uns KEINE verfuegbare Hoehe
         durch, height.js postet nur einseitig). Daher: iframe auf feste Hoehe
         (vario-iframe.ts fixed/LISTING_STATUS_HEIGHT), und der GESAMTE Slot-
         Inhalt scrollt in .kls-shell — egal wie hoch der einzelne Tab ist.
         html/body/main auf 100% klemmen, damit .kls-shell der einzige Scroll-
         Container ist (bridge.css setzt diese ohnehin auf overflow:hidden). */
      html, body { height: 100% !important; margin: 0 !important; overflow: hidden !important; }
      main#app, main#app > section#content { height: 100% !important; overflow: hidden !important; }
      /* Verbindungs-Punkt der App-Shell (patterns.css .status-dot, position:absolute
         oben rechts) ausblenden — der Slot hat seine eigene Statuszeile (#slot-status),
         der Punkt klebte sonst ueber dem Slot-Header. */
      #status-dot { display: none !important; }

      /* Slot-Window-Design analog Brand-Layer (kl-modal / kl-form-row / v-banner). */
      .kls-shell {
        display: flex; flex-direction: column; gap: 12px;
        padding: 16px;
        font-family: 'Outfit', system-ui, sans-serif;
        color: #101828;
        /* DER eine Scroll-Container des Slots: fuellt die feste iframe-Hoehe und
           scrollt den gesamten Inhalt (Header + Aktionen + Configure-Pane).
           100vh statt 100% — unabhaengig von der html/body/main-height-Kette.
           box-sizing explizit, da das Frontend keinen globalen border-box-Reset hat,
           sonst sprengt das 16px-Padding die 100vh. */
        height: 100vh; box-sizing: border-box; overflow-y: auto;
      }
      .kls-head {
        display: flex; align-items: flex-start; justify-content: space-between;
        gap: 12px;
      }
      .kls-head h3 {
        margin: 0; font-size: 16px; font-weight: 600; color: #101828;
      }
      .kls-subtitle { font-size: 12px; color: #475467; margin-top: 2px; }

      .kls-current {
        background: #F9FAFB;
        border: 1px solid #E4E7EC;
        border-radius: 8px;
        padding: 10px 12px;
      }
      .kls-current-label {
        font-size: 10.5px; color: #667085;
        text-transform: uppercase; letter-spacing: 0.06em;
        margin-bottom: 4px;
      }
      .kls-current-main {
        display: flex; align-items: center; gap: 8px; flex-wrap: wrap;
        font-weight: 600; font-size: 13.5px;
      }
      .kls-current-secondary { font-size: 11.5px; color: #475467; margin-top: 4px; }

      .kls-section-title {
        font-weight: 600; font-size: 13px; color: #101828;
        margin-bottom: 6px;
      }
      .kls-meta {
        background: #F9FAFB;
        border: 1px solid #E4E7EC;
        border-radius: 8px;
        padding: 10px 12px;
        font-size: 12.5px;
      }
      .kls-meta table { width: 100%; border-collapse: collapse; }
      .kls-meta th {
        text-align: left; width: 38%; padding: 3px 0;
        font-weight: 500; color: #667085;
      }
      .kls-meta td { padding: 3px 0; }
      .kls-meta code {
        font-family: 'JetBrains Mono', ui-monospace, monospace;
        font-size: 12px; color: #1849A9;
      }

      .kls-choice-card {
        border: 1px solid #E4E7EC;
        border-radius: 8px;
        padding: 12px 14px;
        background: #fff;
        display: flex; flex-direction: column; gap: 8px;
        transition: border-color 140ms ease, box-shadow 140ms ease;
      }
      .kls-choice-card:hover {
        border-color: #D0D5DD;
        box-shadow: 0 1px 4px rgba(16,24,40,0.06);
      }
      .kls-choice-title { font-weight: 600; font-size: 13.5px; }
      .kls-choice-desc { font-size: 12px; color: #475467; line-height: 1.45; }
      .kls-choice-actions { display: flex; justify-content: flex-end; }

      .kls-mode-head {
        display: flex; align-items: center; gap: 8px;
        margin-bottom: 10px;
      }
      .kls-mode-head strong { font-size: 13.5px; }
      .kls-back-btn {
        width: 28px; height: 28px;
        display: inline-flex; align-items: center; justify-content: center;
        background: transparent; border: 1px solid transparent;
        border-radius: 6px; cursor: pointer; color: #475467;
        transition: border-color 140ms ease, background 140ms ease;
      }
      .kls-back-btn:hover { border-color: #D0D5DD; background: #F9FAFB; }
      .kls-back-btn .v-icon { font-size: 12px; }

      .kls-actions-row {
        display: flex; justify-content: flex-end; gap: 8px;
        margin-top: 12px; flex-wrap: wrap;
      }
      .kls-actions-row.split { justify-content: space-between; }

      .kls-btn {
        display: inline-flex; align-items: center; gap: 6px;
        height: 32px; padding: 0 14px;
        border-radius: 6px; cursor: pointer; font: inherit; font-size: 12.5px;
        transition: filter 140ms ease, background 140ms ease, border-color 140ms ease;
      }
      .kls-btn--primary {
        background: linear-gradient(180deg, #1A1D21 0%, #0D0F11 100%);
        color: #fff; border: 1px solid #0D0F11;
        box-shadow: 0 1px 2px rgba(26,29,33,0.20);
      }
      .kls-btn--primary:hover { filter: brightness(0.96); }
      .kls-btn--primary[disabled] { opacity: 0.55; cursor: not-allowed; filter: none; }
      .kls-btn--secondary {
        background: #fff; color: #344054; border: 1px solid #D0D5DD;
      }
      .kls-btn--secondary:hover { border-color: #98A2B3; background: #F9FAFB; }
      .kls-btn--ghost {
        background: transparent; color: #475467; border: 1px solid transparent;
      }
      .kls-btn--ghost:hover { border-color: #D0D5DD; background: #F9FAFB; }

      .kls-banner {
        display: flex; align-items: flex-start; gap: 10px;
        padding: 10px 12px;
        border-radius: 8px;
        border: 1px solid transparent;
        font-size: 12.5px; line-height: 1.45;
      }
      .kls-banner--ok { background: #E6F4EA; border-color: #ABEFC6; color: #15803D; }
      .kls-banner--info { background: #EFF4FF; border-color: #B2CCFF; color: #1849A9; }
      .kls-banner .v-icon { font-size: 14px; margin-top: 1px; }

      .kls-error-text {
        font-size: 11.5px; color: #B42318; margin-top: 4px;
      }

      .kls-search-input {
        display: flex; align-items: center; gap: 8px;
        background: #fff; border: 1px solid #D0D5DD;
        border-radius: 6px; height: 36px; padding: 0 12px;
        transition: border-color 140ms ease, box-shadow 140ms ease;
      }
      .kls-search-input:focus-within {
        border-color: #1A1D21;
        box-shadow: 0 0 0 3px rgba(26,29,33,0.18);
      }
      .kls-search-input > .v-icon { color: #98A2B3; font-size: 12px; }
      .kls-search-input > input {
        flex: 1 1 auto; border: 0; outline: none; background: transparent;
        height: 100%; font: inherit; font-size: 13px; color: #101828;
      }

      .kls-input-preview {
        padding: 10px 12px;
        background: #fff;
        border: 1px dashed #D0D5DD;
        border-radius: 8px;
        font-size: 12.5px; line-height: 1.45;
        color: #475467;
      }

      .kls-suggestions-head {
        display: flex; align-items: center; justify-content: space-between;
        margin-bottom: 6px;
      }
      .kls-suggestions-head strong { font-size: 13px; }
    </style>

    <div class="kls-shell">

      <div class="kls-head">
        <div>
          <h3>Kaufland-Kategorie</h3>
          <div id="slot-subtitle" class="kls-subtitle"></div>
        </div>
        <div id="slot-status"></div>
      </div>

      <div id="current-section" hidden class="kls-current">
        <div class="kls-current-label">Aktuell zugeordnet</div>
        <div class="kls-current-main">
          <div id="current-label"></div>
          <span id="current-live-pill" hidden class="v-pill v-pill--ok" style="font-size:10.5px;font-weight:500;text-transform:none;letter-spacing:0;">
            <span class="v-icon fa-regular fa-circle-check" style="margin-right:0.25em;"></span>laufendes Angebot
          </span>
        </div>
        <div id="current-secondary" class="kls-current-secondary"></div>
      </div>

      <div id="mode-configure" hidden>
        <div class="kls-actions-row" style="margin-top:0;">
          <div style="display:flex;gap:8px;flex-wrap:wrap;">
            <button type="button" class="kls-btn kls-btn--secondary" id="btn-release-images">
              <span class="v-icon fa-regular fa-images"></span> Bilder freigeben
            </button>
            <button type="button" class="kls-btn kls-btn--secondary" id="btn-reassign">
              <span class="v-icon fa-regular fa-pen-to-square"></span> Kategorie ändern
            </button>
          </div>
        </div>
        <div id="configure-pane" class="kls-configure-pane" style="margin-top:10px;border:1px solid #E4E7EC;border-radius:8px;background:#fff;"></div>
      </div>

      <div id="mode-choice">
        <div class="kls-section-title">Was möchtest du tun?</div>

        <div style="display:flex;flex-direction:column;gap:10px;">
          <div class="kls-meta">
            <div style="font-weight:600;margin-bottom:6px;">Artikel-Daten aus Vario</div>
            <table>
              <tbody>
                <tr><th>EAN</th><td><code id="choice-ean">—</code></td></tr>
                <tr><th>Hersteller</th><td id="choice-manufacturer">—</td></tr>
              </tbody>
            </table>
            <div id="choice-no-manufacturer" hidden class="kls-error-text">
              Bitte Hersteller in den Artikeldetails pflegen.
            </div>
            <div id="choice-no-ean" hidden class="kls-error-text">
              Bitte EAN in den Artikeldetails oder in diesem Listing weiter unten pflegen.
            </div>
          </div>

          <div id="choice-variant-parent-note" hidden class="kls-meta">
            <div style="font-weight:600;margin-bottom:4px;">
              <span class="v-icon fa-regular fa-sitemap"></span> Variantenartikel
            </div>
            <div>
              Dieser Artikel ist ein Variantenvater. Er wird bei Kaufland nicht selbst als
              Produkt gelistet — jede Variante ist ein eigenes Kaufland-Produkt mit eigener
              EAN. Am Vater wird nur die Kategorie zugeordnet; die Varianten legst du danach
              im Reiter „Varianten“ an. Eine EAN am Vater ist nicht nötig.
            </div>
          </div>

          <div id="choice-card-attach" class="kls-choice-card">
            <div class="kls-choice-title">Auf bestehendes Angebot aufschalten</div>
            <div class="kls-choice-desc">Sucht den Artikel per EAN im Kaufland-Katalog. Treffer ⇒ Kategorie wird übernommen.</div>
            <div class="kls-choice-actions">
              <button type="button" class="kls-btn kls-btn--primary" id="btn-lookup">
                <span class="v-icon fa-regular fa-magnifying-glass"></span> Artikel suchen
              </button>
            </div>
          </div>

          <div class="kls-choice-card">
            <div class="kls-choice-title">Neu listen</div>
            <div class="kls-choice-desc">Kaufland kennt den Artikel noch nicht. Wir schlagen eine Kategorie vor (oder du suchst manuell).</div>
            <div class="kls-choice-actions">
              <button type="button" class="kls-btn kls-btn--secondary" id="btn-new-listing">
                <span class="v-icon fa-regular fa-plus"></span> Neu listen
              </button>
            </div>
          </div>
        </div>
      </div>

      <div id="mode-lookup" hidden>
        <div class="kls-mode-head">
          <button type="button" class="kls-back-btn" id="btn-back-to-choice-1" title="Zurück" aria-label="Zurück">
            <span class="v-icon fa-light fa-arrow-left"></span>
          </button>
          <strong>Kaufland-Katalog</strong>
        </div>
        <div id="lookup-result"></div>
        <div class="kls-actions-row split">
          <button type="button" class="kls-btn kls-btn--ghost" id="btn-lookup-new-instead">
            <span class="v-icon fa-regular fa-plus"></span> Doch neu listen
          </button>
          <button type="button" class="kls-btn kls-btn--primary" id="btn-lookup-take-over" disabled>
            <span class="v-icon fa-regular fa-check"></span> Auf dieses Angebot aufschalten
          </button>
        </div>
      </div>

      <div id="mode-release-images" hidden>
        <div class="kls-mode-head">
          <button type="button" class="kls-back-btn" id="btn-back-from-images" title="Zurück" aria-label="Zurück">
            <span class="v-icon fa-light fa-arrow-left"></span>
          </button>
          <strong>Bilder für Kaufland freigeben</strong>
        </div>
        <div class="kls-subtitle" style="margin-bottom:10px;">
          Wähle die Vario-DMS-Bilder aus, die im Kaufland-Lister-Mapping als Quelle angeboten werden sollen.
          Reihenfolge per Pfeil. Nicht freigegebene Bilder erscheinen im Lister nicht.
        </div>
        <div id="release-images-list" style="margin-bottom:10px;"></div>
        <div class="kls-actions-row">
          <button type="button" class="kls-btn kls-btn--primary" id="btn-save-images">
            <span class="v-icon fa-regular fa-check"></span> Auswahl speichern
          </button>
        </div>
      </div>

      <div id="mode-new-listing" hidden>
        <div class="kls-mode-head">
          <button type="button" class="kls-back-btn" id="btn-back-to-choice-2" title="Zurück" aria-label="Zurück">
            <span class="v-icon fa-light fa-arrow-left"></span>
          </button>
          <strong>Neu listen</strong>
        </div>

        <div id="input-preview" hidden class="kls-input-preview" style="margin-bottom:10px;"></div>

        <div style="margin-bottom:12px;">
          <div class="kls-suggestions-head">
            <strong>Kaufland-Vorschläge</strong>
            <button type="button" class="kls-btn kls-btn--primary" id="btn-decide">
              <span class="v-icon fa-regular fa-lightbulb"></span> Vorschläge holen
            </button>
          </div>
          <div id="suggestions"></div>
        </div>

        <div>
          <strong style="font-size:13px;">Manuell suchen</strong>
          <div class="kls-search-input" style="margin-top:6px;">
            <span class="v-icon fa-light fa-magnifying-glass"></span>
            <input id="search-input" type="search" placeholder="Kategorie-Name oder -ID …" autocomplete="off" />
          </div>
          <div id="search-results" style="margin-top:6px;"></div>
        </div>
      </div>
    </div>
  `;
}

function readSlotParams(): { articleId: string | null; listingId: string | null } {
  const q = new URL(window.location.href).searchParams;
  return {
    articleId: q.get('articleId') ?? q.get('article_id') ?? q.get('id'),
    listingId: q.get('listingId') ?? q.get('listing_id'),
  };
}

function setStatusBar(host: HTMLElement, kind: 'loading' | 'success' | 'error' | '', msg: string): void {
  const el = host.querySelector<HTMLElement>('#slot-status');
  if (!el) return;
  if (!kind) { el.innerHTML = ''; return; }
  const icon = kind === 'loading' ? 'fa-spinner-third fa-spin' : kind === 'success' ? 'fa-circle-check' : 'fa-circle-xmark';
  const cls = kind === 'success' ? 'v-text-success' : kind === 'error' ? 'v-text-error' : 'v-text-muted';
  el.innerHTML = `<span class="${cls}" style="font-size:0.88em;"><span class="v-icon fa-regular ${icon}"></span> ${escapeHtml(msg)}</span>`;
}

function setSubtitle(text: string): void {
  const el = document.querySelector<HTMLElement>('#slot-subtitle');
  if (el) el.textContent = text;
}

function escapeHtml(s: string | null | undefined): string {
  if (s === null || s === undefined) return '';
  return String(s)
    .replaceAll('&', '&amp;')
    .replaceAll('<', '&lt;')
    .replaceAll('>', '&gt;')
    .replaceAll('"', '&quot;')
    .replaceAll("'", '&#39;');
}
